import React, { useEffect, useState, useRef } from 'react';
import ReactPlayer from 'react-player';
import { PlayIcon, PauseIcon, RotateCcwIcon, RotateCwIcon, LoaderIcon } from 'lucide-react';
interface VideoPlayerProps {
  url: string;
  onTimeUpdate: (time: number) => void;
  onPlayPause: (playing: boolean) => void;
  seekTo: number;
}
export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  onTimeUpdate,
  onPlayPause,
  seekTo
}) => {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0);
  const [buffering, setBuffering] = useState(false);
  const [ready, setReady] = useState(false);
  const playerRef = useRef<ReactPlayer | null>(null);
  useEffect(() => {
    if (playerRef.current && Math.abs(seekTo - played * duration) > 1) {
      playerRef.current.seekTo(seekTo / duration, 'fraction');
    }
  }, [seekTo, duration, played]);
  const handleProgress = (state: {
    played: number;
    playedSeconds: number;
    loaded: number;
    loadedSeconds: number;
  }) => {
    setPlayed(state.played);
    onTimeUpdate(state.playedSeconds);
  };
  const handlePlayPause = () => {
    setPlaying(!playing);
    onPlayPause(!playing);
  };
  const handleSeekBackward = () => {
    if (playerRef.current) {
      const currentTime = played * duration;
      const newTime = Math.max(0, currentTime - 10);
      playerRef.current.seekTo(newTime / duration, 'fraction');
      onTimeUpdate(newTime);
    }
  };
  const handleSeekForward = () => {
    if (playerRef.current) {
      const currentTime = played * duration;
      const newTime = Math.min(duration, currentTime + 10);
      playerRef.current.seekTo(newTime / duration, 'fraction');
      onTimeUpdate(newTime);
    }
  };
  const handleBuffer = () => {
    setBuffering(true);
  };
  const handleBufferEnd = () => {
    setBuffering(false);
  };
  const handleReady = () => {
    setReady(true);
    setBuffering(false);
  };
  return <div className="relative aspect-video bg-black rounded-t-lg overflow-hidden" data-id="element-161">
      <ReactPlayer ref={playerRef} url={url} width="100%" height="100%" playing={playing} onProgress={handleProgress} onDuration={setDuration} onBuffer={handleBuffer} onBufferEnd={handleBufferEnd} onReady={handleReady} controls={false} config={{
      youtube: {
        playerVars: {
          modestbranding: 1
        }
      }
    }} data-id="element-162" />
      {/* Buffering overlay */}
      {(buffering || !ready) && <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20" data-id="element-163">
          <div className="text-center" data-id="element-164">
            <LoaderIcon size={48} className="text-white animate-spin mx-auto" data-id="element-165" />
            <div className="text-white mt-2 font-medium" data-id="element-166">Buffering...</div>
          </div>
        </div>}
      {/* Custom controls */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black to-transparent" data-id="element-167">
        <div className="flex items-center justify-center space-x-4" data-id="element-168">
          <button onClick={handleSeekBackward} className="text-white p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors" aria-label="Rewind 10 seconds" data-id="element-169">
            <RotateCcwIcon size={24} data-id="element-170" />
          </button>
          <button onClick={handlePlayPause} className="text-white bg-white bg-opacity-20 p-3 rounded-full hover:bg-opacity-30 transition-colors" aria-label={playing ? 'Pause' : 'Play'} data-id="element-171">
            {playing ? <PauseIcon size={24} data-id="element-172" /> : <PlayIcon size={24} data-id="element-173" />}
          </button>
          <button onClick={handleSeekForward} className="text-white p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors" aria-label="Forward 10 seconds" data-id="element-174">
            <RotateCwIcon size={24} data-id="element-175" />
          </button>
        </div>
      </div>
    </div>;
};